import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { unwrap, errMsg, btnPrimary, btnGhost, inputClass, textareaClass, CAPABILITY_BADGE } from './shared';

export function ConnectionPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/linkedin-ai/capabilities').then((r) => setData(unwrap(r))).catch((e) => setData({ error: errMsg(e) })); }, []);
  if (!data) return <p className="text-[13px] text-[#5C5C5C]">Loading…</p>;
  if (data.error) return <p className="text-[13px] text-[#BA0517]">{data.error}</p>;
  return (
    <div className="space-y-4">
      <div className="sf-card p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[13px] font-bold">{data.connected ? 'LinkedIn account connected' : 'No LinkedIn account connected'}</p>
          <p className="text-[12px] text-[#5C5C5C]">{(data.accounts || []).map((a) => `${a.name || 'Account'} (${a.status})`).join(', ') || 'Skills still work without it: you can paste content in and copy results out.'}</p>
        </div>
        <Link to="/dashboard/linkedin" className={btnGhost + ' inline-flex items-center'}>Manage connection</Link>
      </div>
      <div className="sf-card overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead><tr className="text-left text-[#706E6B] border-b border-[#E5E5E5]"><th className="p-3">Capability</th><th className="p-3">Status</th><th className="p-3">What it means / what to do</th></tr></thead>
          <tbody>
            {data.capabilities.map((c) => (
              <tr key={c.key} className="border-b border-[#F0F0F0] align-top">
                <td className="p-3 font-semibold">{c.label}{c.product && <div className="font-normal text-[#706E6B]">{c.product}</div>}</td>
                <td className="p-3"><span className={`sf-badge ${CAPABILITY_BADGE[c.status]?.[0]}`}>{CAPABILITY_BADGE[c.status]?.[1]}</span></td>
                <td className="p-3 text-[#444]">{c.reason}{c.status !== 'available' && c.manualWorkflow && <div className="mt-1 text-[#0176D3]">Manual workflow: {c.manualWorkflow}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const split = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);

export function BrandVoicePanel() {
  const [v, setV] = useState(null);
  const [samples, setSamples] = useState('');
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    api.get('/linkedin-ai/brand-voice').then((r) => {
      const b = unwrap(r).brandVoice || {};
      setV({ tone: b.tone || '', audience: b.audience || '', preferredVocabulary: (b.preferredVocabulary || []).join('\n'), avoidVocabulary: (b.avoidVocabulary || []).join('\n'), prohibitedClaims: (b.prohibitedClaims || []).join('\n'), formattingPreferences: b.formattingPreferences || '' });
    }).catch((e) => alertService.error('Could not load', errMsg(e)));
  }, []);
  if (!v) return <p className="text-[13px] text-[#5C5C5C]">Loading…</p>;
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const derive = async () => {
    setBusy(true);
    try {
      const r = unwrap(await api.post('/linkedin-ai/brand-voice/derive', { samples: samples.split('\n---\n').map((s) => s.trim()).filter(Boolean) }, { timeout: 120000 }));
      const p = r.output.profile;
      setV({ ...v, tone: p.tone, audience: p.audience, preferredVocabulary: p.preferredVocabulary.join('\n'), avoidVocabulary: p.avoidVocabulary.join('\n'), formattingPreferences: p.formattingPreferences });
      alertService.toast('success', 'Proposal loaded below. Review, edit, then save.');
    } catch (e) { alertService.error('Could not analyse samples', errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put('/linkedin-ai/brand-voice', { tone: v.tone, audience: v.audience, formattingPreferences: v.formattingPreferences, preferredVocabulary: split(v.preferredVocabulary), avoidVocabulary: split(v.avoidVocabulary), prohibitedClaims: split(v.prohibitedClaims) });
      alertService.success('Brand voice saved');
    } catch (e) { if (e?.response?.status === 403) setDenied(true); alertService.error('Could not save', errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="sf-card p-4 space-y-3 max-w-3xl">
      <p className="text-[12px] text-[#5C5C5C]">Used as context by every skill. Only admins can change it.{denied && ' You do not have permission to save.'}</p>
      <label className="block text-[12px] font-semibold">Tone<input className={inputClass} value={v.tone} onChange={set('tone')} maxLength={500} /></label>
      <label className="block text-[12px] font-semibold">Audience<input className={inputClass} value={v.audience} onChange={set('audience')} maxLength={500} /></label>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block text-[12px] font-semibold">Preferred words (one per line)<textarea className={textareaClass} value={v.preferredVocabulary} onChange={set('preferredVocabulary')} /></label>
        <label className="block text-[12px] font-semibold">Words to avoid<textarea className={textareaClass} value={v.avoidVocabulary} onChange={set('avoidVocabulary')} /></label>
        <label className="block text-[12px] font-semibold">Prohibited claims<textarea className={textareaClass} value={v.prohibitedClaims} onChange={set('prohibitedClaims')} /></label>
      </div>
      <label className="block text-[12px] font-semibold">Formatting preferences<input className={inputClass} value={v.formattingPreferences} onChange={set('formattingPreferences')} maxLength={500} /></label>
      <button className={btnPrimary} disabled={busy} onClick={save}>Save brand voice</button>
      <details className="border-t border-[#E5E5E5] pt-3 text-[12px]">
        <summary className="cursor-pointer font-semibold text-[#0176D3]">Build from your own writing</summary>
        <p className="my-2 text-[#5C5C5C]">Paste up to 8 of your own posts, separated by a line containing only ---. The AI proposes a profile; nothing is saved until you press Save.</p>
        <textarea className={textareaClass} value={samples} onChange={(e) => setSamples(e.target.value)} />
        <button className={btnGhost + ' mt-2'} disabled={busy || !samples.trim()} onClick={derive}>Analyse samples</button>
      </details>
    </div>
  );
}

export function HistoryPanel() {
  const [runs, setRuns] = useState(null);
  const [usage, setUsage] = useState([]);
  useEffect(() => {
    api.get('/linkedin-ai/runs', { params: { limit: 50 } }).then((r) => setRuns(unwrap(r).runs || [])).catch(() => setRuns([]));
    api.get('/linkedin-ai/usage').then((r) => setUsage(unwrap(r).bySkill || [])).catch(() => {});
  }, []);
  return (
    <div className="space-y-4">
      <div className="sf-card overflow-x-auto"><table className="w-full text-[12px]">
        <thead><tr className="text-left text-[#706E6B] border-b border-[#E5E5E5]"><th className="p-3">Skill (30 days)</th><th className="p-3">Runs</th><th className="p-3">Failed</th><th className="p-3">Tokens</th><th className="p-3">Credits</th><th className="p-3">Avg time</th></tr></thead>
        <tbody>{usage.length === 0 ? <tr><td className="p-3 text-[#5C5C5C]" colSpan={6}>No usage yet.</td></tr> : usage.map((u) => <tr key={u.skillId} className="border-b border-[#F0F0F0]"><td className="p-3">{u.skillId}</td><td className="p-3">{u.runs}</td><td className="p-3">{u.failed}</td><td className="p-3">{u.tokens}</td><td className="p-3">{u.credits ?? 0}</td><td className="p-3">{(u.avgDurationMs / 1000).toFixed(1)}s</td></tr>)}</tbody>
      </table></div>
      <div className="sf-card overflow-x-auto"><table className="w-full text-[12px]">
        <thead><tr className="text-left text-[#706E6B] border-b border-[#E5E5E5]"><th className="p-3">When</th><th className="p-3">Skill</th><th className="p-3">Status</th><th className="p-3">Provider</th><th className="p-3">Tokens</th><th className="p-3">Error</th></tr></thead>
        <tbody>{runs === null ? <tr><td className="p-3">Loading…</td></tr> : runs.length === 0 ? <tr><td className="p-3 text-[#5C5C5C]" colSpan={6}>No runs yet.</td></tr> : runs.map((r) => <tr key={r._id} className="border-b border-[#F0F0F0]"><td className="p-3">{new Date(r.createdAt).toLocaleString()}</td><td className="p-3">{r.skillId}</td><td className="p-3"><span className={`sf-badge ${r.status === 'succeeded' ? 'sf-badge-success' : r.status === 'failed' ? 'sf-badge-error' : 'sf-badge-warning'}`}>{r.status}</span></td><td className="p-3">{r.provider || '—'}{r.fellBack ? ' (fallback)' : ''}</td><td className="p-3">{r.usage?.totalTokens ?? 0}</td><td className="p-3 text-[#BA0517]">{r.errorMessage || ''}</td></tr>)}</tbody>
      </table></div>
    </div>
  );
}

export function SkillPermissionsPanel() {
  const [data, setData] = useState(null);
  const [disabled, setDisabled] = useState([]);
  const [roles, setRoles] = useState({}); // skillId -> roles[] (absent = default policy)
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/linkedin-ai/settings').then((r) => {
      const d = unwrap(r); setData(d);
      setDisabled(d.settings?.disabledSkills || []); setRoles(d.settings?.roleOverrides || {});
    }).catch((e) => setData({ error: errMsg(e) }));
  }, []);
  if (!data) return <p className="text-[13px] text-[#5C5C5C]">Loading…</p>;
  if (data.error) return <p className="text-[13px] text-[#BA0517]">{data.error}</p>;
  if (!data.canManage) return <p className="sf-card p-4 text-[13px] text-[#5C5C5C]">Only workspace admins can change skill permissions.</p>;

  const toggle = (id) => setDisabled((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));
  const toggleRole = (id, role) => setRoles((r) => {
    const cur = r[id] || null;
    if (!cur) return { ...r, [id]: [role] };
    const next = cur.includes(role) ? cur.filter((x) => x !== role) : [...cur, role];
    return { ...r, [id]: next };
  });
  const reset = (id) => setRoles((r) => { const n = { ...r }; delete n[id]; return n; });
  const save = async () => {
    setBusy(true);
    try { await api.put('/linkedin-ai/settings', { disabledSkills: disabled, roleOverrides: roles }); alertService.success('Skill permissions saved'); } catch (e) { alertService.error('Could not save', errMsg(e)); } finally { setBusy(false); }
  };
  const shown = data.roles.filter((r) => !['admin', 'tenant_admin'].includes(r));
  return (
    <div className="space-y-3">
      <p className="text-[12px] text-[#5C5C5C]">Default: managers, editors and analysts can run skills; viewers cannot; only managers and admins approve and publish. Tick roles to override per skill. Admins can always run enabled skills. A skill switched off here is hidden from everyone in your workspace.</p>
      <div className="sf-card overflow-x-auto"><table className="w-full text-[12px]">
        <thead><tr className="text-left text-[#706E6B] border-b border-[#E5E5E5]"><th className="p-3">Skill</th><th className="p-3">Enabled</th>{shown.map((r) => <th key={r} className="p-3 capitalize">{r}</th>)}<th className="p-3" /></tr></thead>
        <tbody>
          {data.skills.map((s) => (
            <tr key={s.id} className="border-b border-[#F0F0F0]">
              <td className="p-3 font-semibold">{s.name}<div className="font-normal text-[#706E6B]">{s.id}</div></td>
              <td className="p-3"><input type="checkbox" aria-label={`Enable ${s.name}`} checked={!disabled.includes(s.id)} onChange={() => toggle(s.id)} /></td>
              {shown.map((r) => <td key={r} className="p-3"><input type="checkbox" aria-label={`${r} can run ${s.name}`} disabled={!roles[s.id]} checked={Boolean(roles[s.id]?.includes(r))} onChange={() => toggleRole(s.id, r)} /></td>)}
              <td className="p-3 whitespace-nowrap">{roles[s.id] ? <button className="text-[#0176D3] font-semibold" onClick={() => reset(s.id)}>Use default</button> : <button className="text-[#0176D3] font-semibold" onClick={() => toggleRole(s.id, 'manager')}>Customize</button>}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <button className={btnPrimary} disabled={busy} onClick={save}>Save permissions</button>
    </div>
  );
}
